import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpClient } from '../api/http';
import { Alert } from '@piggytechng/component-library';
import { authPayload } from '../helpers/utils/auth-payload';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { queryParamBuilder } from '../helpers/utils';
import { UserBalance } from '../types';
import useRolePermissions from './useRolePermissions';

const useCustomers = () => {
  const { restrictActionByRole, canPerformAction, isSuperAdmin } = useRolePermissions({});

  const params = useParams();
  const userIdIn = params.id as string;

  const [customersParams, setCustomersParams] = useState({
    limit: 20,
    query: '',
    activeFilter: '',
  });
  const [customersData, setCustomers] = useState<{
    list: any[];
    currentPage: number | null;
    nextPage: number | null;
    lastPage: number | null;
    total: number | null;
  }>({
    list: [],
    currentPage: 1,
    nextPage: 1,
    lastPage: 0,
    total: 0,
  });

  const canResetSearch = useMemo(() => {
    return customersParams.query || customersParams.activeFilter;
  }, [customersParams]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRunningWalletAction, setisRunningWalletAction] = useState<boolean>(false);
  const [isRunningUserAction, setIsRunningUserAction] = useState<boolean>(false);
  const [isGeneratingOtp, setIsGeneratingOtp] = useState<boolean>(false);
  const [isFetchingFlagHistory, setIsFetchingFlagHistory] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [piggypointsCursor] = useState({
    page: 1,
    limit: 10,
  });

  const { data: piggyPointsForUser, refetch: refetchPiggyPointsForUser } = useQuery({
    queryKey: ['piggyPointsForUser' + userIdIn + piggypointsCursor.page + piggypointsCursor.limit],
    queryFn: () =>
      httpClient
        .get(`admin/user/piggypoints/${userIdIn}/${piggypointsCursor.page}/${piggypointsCursor.limit}`)
        .then((res) => res.data.data),
  });

  const { data: bankList } = useQuery({
    queryKey: ['user-bank-list'],
    queryFn: () => httpClient.get(`admin/user/bank-list`).then((res) => res.data.data.data),
  });

  const verifyBankNumber = async (bank: string, number: string) => {
    try {
      const response = await httpClient.post<any>('admin/user/validate-bank-account', {
        bankCode: bank,
        accountNumber: number,
      });

      if (response.data.status) {
        return response.data.data.accountname;
      }
      return '';
    } catch (error) {
      console.log(error);
      Alert.error('Failed to verify bank account');
      return '';
    }
  };

  const [searchForm, setSearchForm] = useState({
    query: '',
  });

  const [activeFilter, setActiveFilter] = useState<string>(localStorage.getItem('customersFilter') ?? '');

  const handleChange = (e: { target: { name: string; value: string } }) => {
    const { value } = e.target;
    setSearchForm({ query: value });
  };

  const fetchCustomers = async (paramObj?: any, fetchingMore = false, reset = false) => {
    try {
      fetchingMore ? setIsLoadingMore(true) : setIsLoading(true);

      // If reset is true, reset the params to default values
      if (reset) {
        const defaultParams = {
          limit: 10,
          query: '',
          activeFilter: '',
        };
        setCustomersParams(defaultParams);

        paramObj = { ...defaultParams, page: 1 };
      }

      if (!paramObj) {
        paramObj = { ...customersParams };
      }

      if (paramObj.activeFilter && paramObj.query) {
        // If both activeFilter and query are present, we need to format the query
        // to match the expected structure for the API call
        paramObj = { ...paramObj, [paramObj.activeFilter]: paramObj.query };
      }

      // Remove query and activeFilter from the params
      delete paramObj?.query;
      delete paramObj?.activeFilter;

      const response = await httpClient.get(
        `admin/users/all` + queryParamBuilder({ ...paramObj, page: paramObj?.page ?? customersData.nextPage }),
      );
      if (response.data?.status) {
        const { list, currentPage, lastPage, nextPage, total } = response.data.data;
        setCustomers({
          list: fetchingMore ? [...customersData.list, ...list] : list,
          currentPage,
          nextPage,
          lastPage,
          total,
        });
      }
    } catch (error) {
      console.log(error);
      return error;
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const {
    data: singleCustomer,
    isLoading: isLoadingSingleCustomer,
    refetch: refetchSingleCustomer,
  } = useQuery({
    queryKey: [`single-customer-${userIdIn}`],
    enabled: !!userIdIn,
    retry: false,
    queryFn: () => httpClient.get(`admin/user/profile/${userIdIn}`).then((res) => res.data.data),
  });

  const USER_ACTIVITIES_LIMIT = 10;
  const [userActivitiesState, setUserActivitiesState] = useState<{
    list: any[];
    page: number;
    hasMore: boolean;
  }>({ list: [], page: 0, hasMore: true });
  const [isLoadingUserActivities, setIsLoadingUserActivities] = useState<boolean>(false);
  const [isLoadingMoreUserActivities, setIsLoadingMoreUserActivities] = useState<boolean>(false);

  const fetchUserActivities = useCallback(
    async (page = 1, append = false) => {
      if (!userIdIn) return;
      try {
        if (append) setIsLoadingMoreUserActivities(true);
        else setIsLoadingUserActivities(true);

        const response = await httpClient.get(`/admin/user/activities/${userIdIn}/${page}/${USER_ACTIVITIES_LIMIT}`);
        const data = response.data?.data;
        const incoming: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data?.activities?.results)
              ? data.activities.results
              : [];

        setUserActivitiesState((prev) => ({
          list: append ? [...prev.list, ...incoming] : incoming,
          page,
          hasMore: incoming.length === USER_ACTIVITIES_LIMIT,
        }));
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingUserActivities(false);
        setIsLoadingMoreUserActivities(false);
      }
    },
    [userIdIn],
  );

  useEffect(() => {
    if (userIdIn) fetchUserActivities(1, false);
  }, [userIdIn, fetchUserActivities]);

  const loadMoreUserActivities = () => {
    if (userActivitiesState.hasMore && !isLoadingMoreUserActivities) {
      fetchUserActivities(userActivitiesState.page + 1, true);
    }
  };

  const refetchUserActivities = () => fetchUserActivities(1, false);

  const userActivities = userActivitiesState.list;
  const hasMoreUserActivities = userActivitiesState.hasMore;

  const {
    data: UserBalances,
    isLoading: isLoadingUserBalances,
    refetch: refetchUserBalances,
    isRefetching: isRefetchingUserBalances,
  } = useQuery<UserBalance>({
    queryKey: ['user-balances' + userIdIn],
    enabled: !!userIdIn,
    retry: false,
    queryFn: () => httpClient.get(`admin/user/balances/${userIdIn}`).then((res) => res.data.data),
  });

  const setInterest = async (id: string, action: boolean) => {
    try {
      setIsLoading(true);
      const url = `/admin/user/toggle-interest/${id}/${action}`;
      const response: any = await httpClient.get(url);
      if (response.data.status) {
        Alert.success(`User Interest ${action ? 'Enabled' : 'Disabled'}`);
        refetchSingleCustomer();
      }
      return response.data.status;
    } catch (error) {
      console.log(error);
      Alert.error(`Failed To ${action ? 'Enable' : 'Disable'} User Interest`);
    } finally {
      setIsLoading(false);
    }
  };

  const {
    data: userDevices,
    isLoading: isLoadingUserDevices,
    refetch: refetchUserDevices,
  } = useQuery({
    queryKey: ['userdevices' + userIdIn],
    queryFn: () => httpClient.get(`admin/user/fetch-devices/${userIdIn}`).then((res) => res.data.data.deviceList),
  });

  const unlinkUserDevice = async (userId: string, deviceId: string, callback?: () => void) => {
    if (!canPerformAction('manageDevices', 'deleteDevice')) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }

    try {
      setIsLoading(true);
      const url = `admin/user/unlink-device/${userId}/${deviceId}`;
      const response = await httpClient.get(url);
      if (response.data.status) {
        Alert.success('Device unlinked succefully');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
      refetchUserDevices();
      callback?.();
    }
  };

  const creditPiggypoint = async (data: any) => {
    try {
      setIsLoading(true);
      const response = await httpClient.post(`/admin/user/piggypoints/credit`, {
        ...data,
        userId: userIdIn,
      });
      if (response.data.status) {
        Alert.success('Piggypoint credited successfully');
        refetchPiggyPointsForUser();
      }
      return Promise.resolve(true);
    } catch (error) {
      console.error('Error crediting Piggypoint:', error);
      return Promise.reject(error);
    } finally {
      setIsLoading(false);
      refetchUserBalances();
    }
  };

  const debitPiggypoint = async (data: any) => {
    try {
      setIsLoading(true);
      const response = await httpClient.post(`/user/piggypoints/debit`, {
        ...data,
        userId: userIdIn,
      });
      if (response.data.status) {
        Alert.success('Piggypoint debited successfully');
        refetchPiggyPointsForUser();
      }
      return Promise.resolve(true);
    } catch (error) {
      console.error('Error debiting Piggypoint:', error);
      return Promise.reject(error);
    } finally {
      setIsLoading(false);
      refetchUserBalances();
    }
  };

  const handleWalletTransfer = async (
    userId: any,
    payload: {
      amount: number;
      type: string;
      wallet: string;
      memoTemplate: string;
      transactionId: string;
    },
    password: string,
    callback?: () => void,
  ) => {
    try {
      setisRunningWalletAction(true);
      const updatedPayload = { ...payload, ...authPayload(password) };
      const response: any = await httpClient.post(`admin/user/wallet/transfer/${userId}`, updatedPayload);
      if (response.data.status) {
        Alert.success(response.data.message);
        callback?.();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setisRunningWalletAction(false);
    }
  };

  const generateOtp = async (userId: string) => {
    try {
      setIsGeneratingOtp(true);
      const response = await httpClient.get(`admin/user/generate-otp/${userId}`);
      if (response.data.status) {
        Alert.success("OTP Successfully sent to user's device");
        return response.data.data;
      }
    } catch (error: any) {
      console.log(error);
      Alert.error(error?.response?.data?.message || 'Failed to generate OTP');
    } finally {
      setIsGeneratingOtp(false);
    }
  };

  const getFlagHistory = async (userId: string, page = 1, limit = 10) => {
    try {
      setIsFetchingFlagHistory(true);
      const response = await httpClient.get(`admin/user/flag-history/${userId}/${page}/${limit}`);
      if (response.data.status) {
        return response.data.data;
      }
    } catch (error: any) {
      console.log(error);
      Alert.error(error?.response?.data?.message || 'Failed to fetch flag history');
    } finally {
      setIsFetchingFlagHistory(false);
    }
  };

  const flagUser = async (userId: string, payload: any, callback?: () => void) => {
    if (restrictActionByRole('flagAndActions', 'flagAction')) return;
    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post(`admin/user/flag/${userId}`, {
        ...payload,
        ...authPayload(payload.password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'User Flagged Successfully');
        callback?.();
        refetchSingleCustomer();
      }
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to perform the action');
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const unflagUser = async (userId: string, payload: any, callback?: () => void) => {
    if (restrictActionByRole('flagAndActions', 'unflagAction')) return;
    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post<any>(`admin/user/unflag/${userId}`, authPayload(payload.password));
      if (response.data.status) {
        Alert.success(response.data.message || 'User Unflagged Successfully');
        callback?.();
        refetchSingleCustomer();
      }
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to perform the action');
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const deleteUserBank = async (userId: string, payload: any, callback?: () => void) => {
    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post<any>(`admin/user/delete-bank/${userIdIn}`, {
        ...payload,
        ...authPayload(payload.password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Bank Account Deleted Successfully');
        callback?.();
      }
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to perform the action');
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const deleteUserCard = async (userId: string, payload: any, callback?: () => void) => {
    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post<any>(`admin/user/delete-card/${userIdIn}`, {
        ...payload,
        ...authPayload(payload.password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Card Deleted Successfully');
        callback?.();
      }
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to perform the action');
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const runUserAction = async (userId: string, payload: any, callback?: () => void) => {
    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post(`/admin/user/update-data/${userId}`, payload);
      if (callback) {
        callback();
      }
      Alert.success(response?.data?.message || 'Action performed successfully');
      return Promise.resolve(response);
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occured trying to perform the action');
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const updateUserIdentity = async (type: 'bvn' | 'nin', value: string, password: string, callback?: () => void) => {
    if (restrictActionByRole('customerPage', type === 'bvn' ? 'editBvn' : 'editNin')) return false;

    const label = type.toUpperCase();

    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post<any>('admin/user/identity/update', {
        type,
        value,
        userId: userIdIn,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || `${label} updated successfully`);
        callback?.();
        refetchSingleCustomer();
        return true;
      }
      Alert.error(response.data.message || `${label} could not be updated`);
      return false;
    } catch (err: any) {
      console.log(err?.response?.data?.message || `An error occurred trying to update the ${label}`);
      return false;
    } finally {
      setIsRunningUserAction(false);
    }
  };

  // POST admin/user/nin/verify/:userId { nin, identifier, password }. On success the profile
  // query is refetched so the NIN panel fills; on failure the http interceptor already toasts.
  const verifyUserNin = async (nin: string, password: string, callback?: () => void) => {
    if (restrictActionByRole('customerPage', 'verifyNin')) return false;

    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post<any>(`admin/user/nin/verify/${userIdIn}`, {
        nin,
        ...authPayload(password),
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'NIN verified successfully');
        callback?.();
        refetchSingleCustomer();
        return true;
      }
      Alert.error(response.data.message || 'NIN could not be verified');
      return false;
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occurred trying to verify the NIN');
      return false;
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const releaseUserNin = async (password: string, callback?: () => void) => {
    if (restrictActionByRole('customerPage', 'releaseNin')) return false;

    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post<any>(`admin/user/nin/release/${userIdIn}`, authPayload(password));
      if (response.data.status) {
        Alert.success(response.data.message || 'NIN released successfully');
        callback?.();
        refetchSingleCustomer();
        return true;
      }
      Alert.error(response.data.message || 'NIN could not be released');
      return false;
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occurred trying to release the NIN');
      return false;
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const fixWalletBalance = async (wallet: 'piggybank' | 'flexnaira' | 'flexdollar', userId?: string) => {
    // Flex Dollar is superadmin-only; piggybank / flexnaira keep their existing access.
    if (wallet === 'flexdollar' && !isSuperAdmin) {
      Alert.warn('You do not have access to perform this action.');
      return;
    }

    try {
      setIsLoading(true);
      const response = await httpClient.get(`/admin/user/wallet/fix-balances/${wallet}/${userId ?? userIdIn}`);
      if (response.data.status) {
        Alert.success('Wallet balance fixed successfully');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const reverseMultipleAutosave = async (userId: string, date: string, password: string, callback?: () => void) => {
    try {
      setIsLoading(true);

      const auth = authPayload(password);

      const url = `admin/piggybank/reverse-multiple-autosaves/${userId}/${date}`;
      const response = await httpClient.get(url, {
        headers: {
          'x-admin-password': auth.password,
          'x-admin-identifier': auth.identifier,
        },
      });
      if (response.data.status) {
        Alert.success(response.data.message || 'Action successful');
        if (callback) callback();
      }
      return response.data;
    } catch (error: any) {
      console.log(error);
      Alert.error(error?.response?.data?.message || 'Action failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resetLoginCredentials = async (password: string, type: 'pin' | 'password', callback?: () => void) => {
    try {
      setIsRunningUserAction(true);
      const response = await httpClient.post<any>(`admin/user/reset-login-credentials/${userIdIn}`, {
        ...authPayload(password),
        type,
      });
      if (response.data.status) {
        Alert.success(response.data.message || `User ${type} reset successfully`);
        callback?.();
      }
    } catch (err: any) {
      console.log(err?.response?.data?.message || 'An error occurred trying to reset login credentials');
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const setWithdrawalDelay = async (userId: string, hours: number) => {
    if (restrictActionByRole('flagAndActions', 'setWithdrawalDelay')) return;
    try {
      setIsRunningUserAction(true);
      const response = await httpClient.get(`admin/user/set-withdrawal-delay/${userId}/${hours}`);
      if (response.data.status) {
        Alert.success(response.data.message || 'Withdrawal delay set successfully');
      }
      return response.data;
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsRunningUserAction(false);
    }
  };

  const clearFilter = () => {
    localStorage.setItem('activeCustomer', '');
    setActiveFilter('');
    setSearchForm({ query: '' });
  };

  return {
    fetchCustomers,
    customersData,
    customersParams,
    setCustomersParams,
    singleCustomer,
    isLoadingSingleCustomer,
    refetchSingleCustomer,
    verifyUserNin,
    releaseUserNin,
    updateUserIdentity,
    searchForm,
    setSearchForm,
    activeFilter,
    setActiveFilter,
    handleChange,
    setIsLoading,
    isLoading,
    isLoadingMore,
    setInterest,
    userDevices,
    unlinkUserDevice,
    clearFilter,
    isLoadingUserDevices,
    piggyPointsForUser,
    userActivities,
    isLoadingUserActivities,
    isLoadingMoreUserActivities,
    hasMoreUserActivities,
    loadMoreUserActivities,
    refetchUserActivities,
    canResetSearch,
    creditPiggypoint,
    debitPiggypoint,

    UserBalances,
    fixWalletBalance,
    isLoadingUserBalances,
    refetchUserBalances,
    isRefetchingUserBalances,
    isRunningWalletAction,
    handleWalletTransfer,
    flagUser,
    unflagUser,
    deleteUserBank,
    deleteUserCard,
    runUserAction,
    isRunningUserAction,
    resetLoginCredentials,
    setWithdrawalDelay,
    reverseMultipleAutosave,
    generateOtp,
    isGeneratingOtp,
    getFlagHistory,
    isFetchingFlagHistory,
    bankList,
    verifyBankNumber,
  };
};

export default useCustomers;
